/**
 * Rugged Horizon Overland — Custom JavaScript
 * Shopify Dawn OS2.0
 * Vanilla JS only, no dependencies
 */

/* ============================================================
   VehicleFinder
   ============================================================ */
class VehicleFinder {
  constructor(containerSelector) {
    this.containers = document.querySelectorAll(containerSelector || '[data-vehicle-finder]');
    if (!this.containers.length) return;
    this.data = {
      'Jeep':        ['Wrangler', 'Gladiator', 'Cherokee', 'Grand Cherokee'],
      'Toyota':      ['4Runner', 'Tacoma', 'Land Cruiser', 'Tundra', 'Sequoia', 'FJ Cruiser'],
      'Ford':        ['Bronco', 'F-150', 'Ranger', 'Expedition'],
      'Ram':         ['1500', '2500', '3500'],
      'Chevy':       ['Colorado', 'Silverado 1500', 'Suburban', 'Tahoe'],
      'Sprinter Van':['144" WB', '170" WB', '170" EXT WB'],
      'Transit Van': ['148" WB', '148" EXT WB', '350 HD'],
      'Nissan':      ['Frontier', 'Titan', 'Xterra'],
    };
    this.yearStart = 2010;
    this.yearEnd = new Date().getFullYear() + 1;
    this.init();
  }

  init() {
    this.containers.forEach(container => {
      const makeSelect  = container.querySelector('[data-vehicle-make]');
      const modelSelect = container.querySelector('[data-vehicle-model]');
      const yearSelect  = container.querySelector('[data-vehicle-year]');
      const form        = container.querySelector('form, [data-vehicle-form]');

      if (!makeSelect || !modelSelect || !yearSelect) return;

      // Populate makes
      Object.keys(this.data).forEach(make => {
        makeSelect.add(new Option(make, make));
      });

      // Populate years (newest first)
      for (let y = this.yearEnd; y >= this.yearStart; y--) {
        yearSelect.add(new Option(y, y));
      }

      // Restore saved vehicle
      const saved = this.getSaved();
      if (saved) {
        makeSelect.value = saved.make;
        this._populateModels(modelSelect, saved.make);
        modelSelect.value = saved.model;
        yearSelect.value  = saved.year;
      }

      makeSelect.addEventListener('change', () => {
        this._populateModels(modelSelect, makeSelect.value);
      });

      if (form) {
        form.addEventListener('submit', e => {
          e.preventDefault();
          const make  = makeSelect.value;
          const model = modelSelect.value;
          const year  = yearSelect.value;
          if (!make || !model || !year) return;
          this.save({ make, model, year });
          document.dispatchEvent(new CustomEvent('vehicle:selected', {
            detail: { make, model, year }, bubbles: true
          }));
          const slug = `${make}-${model}-${year}`.toLowerCase().replace(/\s+/g, '-').replace(/"/g, '');
          window.location.href = `/collections/all?vehicle=${encodeURIComponent(slug)}`;
        });
      }
    });
  }

  _populateModels(select, make) {
    while (select.options.length > 1) select.remove(1);
    const models = this.data[make] || [];
    models.forEach(m => select.add(new Option(m, m)));
    select.disabled = models.length === 0;
  }

  save(vehicle) {
    try { localStorage.setItem('rh_vehicle', JSON.stringify(vehicle)); } catch(e) {}
  }

  getSaved() {
    try {
      const v = localStorage.getItem('rh_vehicle');
      return v ? JSON.parse(v) : null;
    } catch(e) { return null; }
  }
}

/* ============================================================
   StickyATC
   ============================================================ */
class StickyATC {
  constructor() {
    this.bar = document.querySelector('.sticky-atc');
    this.form = document.querySelector('.product-form');
    if (!this.bar || !this.form) return;

    // Mirror add to cart button
    const mainBtn = this.form.querySelector('[name="add"]');
    const stickyBtn = this.bar.querySelector('.sticky-atc__button');
    if (mainBtn && stickyBtn) {
      stickyBtn.addEventListener('click', () => mainBtn.click());
    }

    // Mirror product title and price
    const titleEl = document.querySelector('.product__title');
    const priceEl = document.querySelector('.price__regular .price-item');
    const stickyTitle = this.bar.querySelector('.sticky-atc__title');
    const stickyPrice = this.bar.querySelector('.sticky-atc__price');
    if (titleEl && stickyTitle) stickyTitle.textContent = titleEl.textContent.trim();
    if (priceEl && stickyPrice) stickyPrice.textContent = priceEl.textContent.trim();

    this._onScroll = this._onScroll.bind(this);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }

  _onScroll() {
    const rect = this.form.getBoundingClientRect();
    const visible = rect.bottom <= 0 || rect.bottom <= window.innerHeight * 0.1;
    this.bar.classList.toggle('sticky-atc--visible', visible);
  }
}

/* ============================================================
   MegaMenu
   ============================================================ */
class MegaMenu {
  constructor() {
    this.triggers = document.querySelectorAll('[data-mega-menu-trigger]');
    if (!this.triggers.length) return;
    this.activeMenu = null;
    this.init();
  }

  init() {
    this.triggers.forEach(trigger => {
      const menuId = trigger.dataset.megaMenuTrigger;
      const menu = document.getElementById(menuId);
      if (!menu) return;

      const isMobile = () => window.innerWidth < 1024;

      trigger.addEventListener('click', e => {
        if (!isMobile()) return;
        e.preventDefault();
        const isOpen = menu.classList.contains('mega-menu--open');
        this._closeAll();
        if (!isOpen) {
          menu.classList.add('mega-menu--open');
          this.activeMenu = menu;
        }
      });

      trigger.addEventListener('mouseenter', () => {
        if (isMobile()) return;
        this._closeAll();
        menu.classList.add('mega-menu--open');
        this.activeMenu = menu;
      });

      // Close on mouse leave from trigger+menu area
      const wrapper = trigger.closest('[data-mega-menu-wrapper]') || trigger.parentElement;
      if (wrapper) {
        wrapper.addEventListener('mouseleave', () => {
          if (isMobile()) return;
          menu.classList.remove('mega-menu--open');
          if (this.activeMenu === menu) this.activeMenu = null;
        });
      }
    });

    // Close on outside click
    document.addEventListener('click', e => {
      if (!this.activeMenu) return;
      const clickedInMenu = this.activeMenu.contains(e.target);
      const clickedTrigger = [...this.triggers].some(t => t.contains(e.target));
      if (!clickedInMenu && !clickedTrigger) this._closeAll();
    });

    // Close on ESC
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this._closeAll();
    });
  }

  _closeAll() {
    document.querySelectorAll('.mega-menu--open').forEach(m => m.classList.remove('mega-menu--open'));
    this.activeMenu = null;
  }
}

/* ============================================================
   SpecsTableToggle
   ============================================================ */
class SpecsTableToggle {
  constructor(threshold = 6) {
    this.threshold = threshold;
    document.querySelectorAll('.product-specs-table').forEach(table => this._init(table));
  }

  _init(table) {
    const rows = table.querySelectorAll('tbody tr');
    if (rows.length <= this.threshold) return;

    // Hide rows beyond threshold
    for (let i = this.threshold; i < rows.length; i++) {
      rows[i].style.display = 'none';
      rows[i].dataset.specsHidden = '1';
    }

    const btn = document.createElement('button');
    btn.className = 'specs-table__toggle';
    btn.textContent = `Show all ${rows.length} specifications ▾`;
    let expanded = false;

    btn.addEventListener('click', () => {
      expanded = !expanded;
      for (let i = this.threshold; i < rows.length; i++) {
        rows[i].style.display = expanded ? '' : 'none';
      }
      btn.textContent = expanded
        ? 'Show fewer specifications ▴'
        : `Show all ${rows.length} specifications ▾`;
    });

    table.parentNode.insertBefore(btn, table.nextSibling);
  }
}

/* ============================================================
   ImageGalleryZoom
   ============================================================ */
class ImageGalleryZoom {
  constructor() {
    this.gallery  = document.querySelector('.product-media-gallery, [data-media-gallery]');
    this.mainImg  = document.querySelector('.product__media img, [data-main-image]');
    if (!this.gallery || !this.mainImg) return;
    this.init();
  }

  init() {
    this.gallery.querySelectorAll('[data-thumbnail], .thumbnail-list__item img').forEach((thumb, i) => {
      thumb.style.cursor = 'pointer';
      thumb.addEventListener('click', () => {
        const src    = thumb.dataset.src || thumb.src;
        const srcset = thumb.dataset.srcset || thumb.srcset || '';
        if (src) {
          this.mainImg.src = src;
          if (srcset) this.mainImg.srcset = srcset;
          this.mainImg.style.cursor = 'zoom-in';
        }
      });
    });

    // Simple lightbox on main image click
    this.mainImg.style.cursor = 'zoom-in';
    this.mainImg.addEventListener('click', () => {
      this._openLightbox(this.mainImg.src);
    });
  }

  _openLightbox(src) {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position:fixed;inset:0;background:rgba(0,0,0,0.9);z-index:9999;
      display:flex;align-items:center;justify-content:center;cursor:zoom-out;
    `;
    const img = document.createElement('img');
    img.src = src;
    img.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;border-radius:4px;';
    overlay.appendChild(img);
    overlay.addEventListener('click', () => overlay.remove());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') overlay.remove(); }, { once: true });
    document.body.appendChild(overlay);
  }
}

/* ============================================================
   SmoothScroll
   ============================================================ */
class SmoothScroll {
  constructor() {
    document.addEventListener('click', e => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
}

/* ============================================================
   ReviewsCarousel
   ============================================================ */
class ReviewsCarousel {
  constructor() {
    document.querySelectorAll('[data-reviews-carousel]').forEach(el => this._init(el));
  }

  _init(carousel) {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev  = carousel.querySelector('[data-carousel-prev]');
    const next  = carousel.querySelector('[data-carousel-next]');
    if (!track) return;

    const scrollBy = () => {
      const card = track.querySelector('.review-card');
      return card ? card.offsetWidth + 20 : 320;
    };

    prev && prev.addEventListener('click', () => {
      track.scrollBy({ left: -scrollBy(), behavior: 'smooth' });
    });
    next && next.addEventListener('click', () => {
      track.scrollBy({ left: scrollBy(), behavior: 'smooth' });
    });
  }
}

/* ============================================================
   VehicleFitmentBadge (product page)
   ============================================================ */
class VehicleFitmentBadge {
  constructor() {
    const badge = document.getElementById('fitment-badge');
    if (!badge) return;

    const productTags = (badge.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
    const fitTags = productTags.filter(t => t.startsWith('fits:'));

    if (!fitTags.length) {
      badge.style.display = 'none';
      return;
    }

    try {
      const saved = JSON.parse(localStorage.getItem('rh_vehicle') || 'null');
      if (saved) {
        const vehicleKey = `fits:${saved.make}-${saved.model}`.toLowerCase().replace(/\s+/g, '-').replace(/"/g, '');
        const fits = fitTags.includes(vehicleKey);
        badge.className = `fitment-badge ${fits ? 'fitment-badge--fits' : 'fitment-badge--unknown'}`;
        badge.innerHTML = fits
          ? `✅ Fits your ${saved.year} ${saved.make} ${saved.model} <a href="#" class="fitment-badge__change" data-vehicle-reset>Change</a>`
          : `⚠️ May not fit your ${saved.year} ${saved.make} ${saved.model} — <a href="#vehicle-finder" class="fitment-badge__change">Check fitment</a>`;
      } else {
        badge.className = 'fitment-badge fitment-badge--unknown';
        badge.innerHTML = `⚠️ <a href="#vehicle-finder">Select your vehicle</a> to check fitment`;
      }
    } catch(e) {
      badge.style.display = 'none';
    }

    // Reset handler
    badge.addEventListener('click', e => {
      if (e.target.dataset.vehicleReset !== undefined) {
        e.preventDefault();
        localStorage.removeItem('rh_vehicle');
        location.reload();
      }
    });
  }
}

/* ============================================================
   Tab Switcher (Best Sellers tabs)
   ============================================================ */
class TabSwitcher {
  constructor() {
    document.querySelectorAll('[data-tabs]').forEach(container => this._init(container));
  }

  _init(container) {
    const buttons = container.querySelectorAll('[data-tab-btn]');
    const panels  = container.querySelectorAll('[data-tab-panel]');

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tabBtn;
        buttons.forEach(b => b.classList.toggle('tab-bar__btn--active', b.dataset.tabBtn === target));
        panels.forEach(p => p.hidden = p.dataset.tabPanel !== target);
      });
    });

    // Activate first tab
    if (buttons.length) buttons[0].click();
  }
}

/* ============================================================
   Announcement Bar
   ============================================================ */
class AnnouncementBar {
  constructor() {
    const bar = document.querySelector('[data-announcement-bar]');
    const close = document.querySelector('[data-announcement-close]');
    if (!bar || !close) return;

    const dismissed = sessionStorage.getItem('rh_announcement_dismissed');
    if (dismissed) { bar.remove(); return; }

    close.addEventListener('click', () => {
      bar.style.maxHeight = bar.offsetHeight + 'px';
      requestAnimationFrame(() => {
        bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
        bar.style.maxHeight = '0';
        bar.style.opacity = '0';
        bar.style.overflow = 'hidden';
      });
      bar.addEventListener('transitionend', () => bar.remove(), { once: true });
      sessionStorage.setItem('rh_announcement_dismissed', '1');
    });
  }
}

/* ============================================================
   Init
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  new VehicleFinder('[data-vehicle-finder]');
  new StickyATC();
  new MegaMenu();
  new SpecsTableToggle(6);
  new ImageGalleryZoom();
  new SmoothScroll();
  new ReviewsCarousel();
  new VehicleFitmentBadge();
  new TabSwitcher();
  new AnnouncementBar();
});
